// ArChat Auto-Normalized File
// Created: 12-03-2026 22:38:36
// Author: SIVOL® - Aldo Frias - Argentina

/**
 * Autor: SIVOL® - Aldo Frias - Argentina
 * Fecha: 11/03/2026 22:50 (Argentina - UTC-3)
 * ARCHAT — Contexto de tema (solo Argentina / Dark)
 * Estados válidos: 'argentina' (institucional) | 'dark'.
 * Antes de login: siempre 'argentina'. Dark solo si usuario logueado lo elige en perfil.
 * @file context/ThemeContext.jsx
 */

import React, { createContext, useContext, useState, useLayoutEffect, useEffect, useCallback, useMemo } from 'react';
import { STORAGE_KEYS } from '../utils/constants';
import { apiService } from '../services/api';
import { useAuth } from './AuthContext';
import { applyTheme as applyThemeDOM } from '../hooks/useTheme';

const THEMES = {
  ARGENTINA: 'argentina',
  DARK: 'dark'
};

const THEME_VALID = ['argentina', 'dark'];
const THEME_STORAGE_KEY = STORAGE_KEYS.THEME;

const log = (msg, detail) => {
  if (typeof console !== 'undefined' && console.log) {
    console.log('[THEME] ' + msg, detail !== undefined ? detail : '');
  }
};

/** Lee tema desde localStorage. Solo 'argentina' o 'dark'; si viene 'light' (legado) se normaliza a 'argentina'. */
const readStoredTheme = () => {
  try {
    const stored = (localStorage.getItem(THEME_STORAGE_KEY) || '').toLowerCase().trim();
    if (stored === 'dark') return THEMES.DARK;
    if (stored === 'argentina') return THEMES.ARGENTINA;
    if (stored === 'light') return THEMES.ARGENTINA;
    return THEMES.ARGENTINA;
  } catch (_) {
    return THEMES.ARGENTINA;
  }
};

const ThemeContext = createContext(null);

export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme debe usarse dentro de ThemeProvider');
  return ctx;
};

export const ThemeProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [theme, setThemeState] = useState(() => readStoredTheme());

  const effectiveTheme = !isAuthenticated ? readStoredTheme() : theme;

  // Tema solo via data-theme en documentElement (hook global para log y consistencia).
  useLayoutEffect(() => {
    if (typeof document === 'undefined') return;
    const themeStr = typeof effectiveTheme === 'string' ? effectiveTheme : THEMES.ARGENTINA;
    applyThemeDOM(themeStr);
  }, [effectiveTheme]);

  /** Solo aplica tema en estado y DOM. No escribe en localStorage. */
  const applyTheme = useCallback((themeValue) => {
    setThemeState(themeValue);
    applyThemeDOM(themeValue);
  }, []);

  /** Al cambiar isAuthenticated: leer archat_theme y aplicar solo si es válido. Sin fallback, sin escribir storage. */
  useEffect(() => {
    const raw = localStorage.getItem(THEME_STORAGE_KEY);
    const isValid =
      raw === THEMES.DARK ||
      raw === THEMES.ARGENTINA;

    if (isValid) {
      applyTheme(raw);
      if (typeof console !== 'undefined' && console.log) {
        console.log('[THEME_APPLY]', raw);
      }
    }

    // Si no hay valor válido en storage, NO forzar Argentina aquí. Mantener el theme actual.
  }, [isAuthenticated, applyTheme]);

  /** Selector manual: aplica tema, persiste en localStorage, persiste en backend (SaaS). Si API falla, solo se loguea. */
  const setTheme = useCallback((value) => {
    if (!isAuthenticated && value === THEMES.DARK) {
      log('setTheme', 'dark bloqueado: usuario no logueado');
      return;
    }
    const next = value && THEME_VALID.includes(String(value).toLowerCase()) ? String(value).toLowerCase() : THEMES.ARGENTINA;
    applyTheme(next);
    localStorage.setItem(THEME_STORAGE_KEY, next);
    if (typeof console !== 'undefined' && console.log) {
      console.log('[THEME_SET]', next);
    }
    apiService.setTheme(next).catch((err) => {
      if (typeof console !== 'undefined' && console.warn) {
        console.warn('[THEME] persist backend failed', err?.message || err);
      }
    });
  }, [isAuthenticated, applyTheme]);

  /** Sincroniza estado desde backend sin escribir archat_theme. archat_theme solo se escribe en setTheme (cambio manual). */
  const syncTheme = useCallback(async (backendTheme, persistToBackend = false) => {
    const normalized = backendTheme === 'light' ? THEMES.ARGENTINA : (backendTheme === 'dark' ? THEMES.DARK : theme);
    const toApply = !isAuthenticated && normalized === THEMES.DARK ? THEMES.ARGENTINA : normalized;
    setThemeState(toApply);
    applyThemeDOM(toApply);
    if (persistToBackend) {
      try {
        await apiService.setTheme(toApply === THEMES.ARGENTINA ? 'argentina' : 'dark');
      } catch (_) {}
    }
  }, [theme, isAuthenticated]);

  const toggleTheme = useCallback(() => {
    const next = theme === THEMES.DARK ? THEMES.ARGENTINA : THEMES.DARK;
    setTheme(next);
  }, [theme, setTheme]);

  const value = useMemo(() => ({
    theme: effectiveTheme,
    preference: effectiveTheme,
    setTheme,
    toggleTheme,
    syncTheme,
    THEMES
  }), [effectiveTheme, setTheme, toggleTheme, syncTheme]);

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export { THEMES };
